import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/auth/AuthLayout';
import { AuthCard } from '../components/auth/AuthCard';
import { OAuthButtons } from '../components/auth/OAuthButtons';
import { Button } from '../components/shared/Button';
import { Eye, EyeOff, UserPlus, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface FormData {
  name: string;
  email: string;
  password: string;
  passwordConfirm: string;
  program: 'I' | 'II';
  acceptTerms: boolean;
  ageConfirm: boolean;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  passwordConfirm?: string;
  acceptTerms?: string;
  ageConfirm?: string;
}

export const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register: apiRegister } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    passwordConfirm: '',
    program: 'II',
    acceptTerms: false,
    ageConfirm: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = t('validation.nameRequired');
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t('validation.nameMin');
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = t('validation.emailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('validation.email');
    }

    // Password validation: 8–32 chars, at least one upper, lower, digit, and one special character
    const pwd = formData.password;
    if (!pwd) {
      newErrors.password = t('validation.password');
    } else if (pwd.length < 8) {
      newErrors.password = t('validation.passwordMin');
    } else if (pwd.length > 32) {
      newErrors.password = t('validation.passwordMax');
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])[\x20-\x7E]+$/.test(pwd)) {
      newErrors.password = t('validation.passwordComplexity');
    }

    // Password confirmation
    if (!formData.passwordConfirm) {
      newErrors.passwordConfirm = t('validation.passwordConfirm');
    } else if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = t('validation.passwordMatch');
    }

    // Terms acceptance
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = t('validation.termsRequired');
    }

    // Minimum age check (GDPR Art. 8 / UAVG Art. 16)
    if (!formData.ageConfirm) {
      newErrors.ageConfirm = t('validation.ageConfirm');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await apiRegister({
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
      });
      setSuccessMessage(t('auth.register.success'));
      setTimeout(() => navigate('/dashboard'), 800);
    } catch (err: unknown) {
      const res = err as { response?: { data?: { message?: string | string[] } } };
      const msg = res.response?.data?.message;
      setErrorMessage(
        Array.isArray(msg) ? msg.join(' ') : (msg as string) || t('auth.register.error')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (errorMessage) setErrorMessage(null);
  };

  const getPasswordStrength = (): { label: string; color: string; width: string } => {
    const { password } = formData;
    if (!password) return { label: '', color: '', width: '0%' };
    if (password.length < 6) return { label: t('auth.passwordStrength.weak'), color: 'bg-error', width: '25%' };
    if (password.length < 8) return { label: t('auth.passwordStrength.fair'), color: 'bg-warning', width: '50%' };
    if (password.length < 12) return { label: t('auth.passwordStrength.good'), color: 'bg-success', width: '75%' };
    return { label: t('auth.passwordStrength.strong'), color: 'bg-success', width: '100%' };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <AuthLayout
      variant="register"
      title={t('auth.register.title')}
      subtitle={t('auth.register.subtitle')}
    >
      <AuthCard
        label={t('auth.register.cardLabel')}
        labelColor="primary"
        title={t('auth.register.cardTitle')}
        subtitle={t('auth.register.cardSubtitle')}
        footer={{
          text: t('auth.register.hasAccount'),
          linkText: t('auth.register.login'),
          href: '/login',
        }}
      >
        {/* Success Message */}
        {successMessage && (
          <div 
            className="flex items-center gap-3 rounded-2xl border border-success/25 bg-success/10 p-4 text-success"
            role="alert"
            aria-live="polite"
          >
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{successMessage}</span>
          </div>
        )}

        {/* Error Message (API errors) */}
        {errorMessage && (
          <div 
            className="flex items-center gap-3 rounded-2xl border border-error/25 bg-error/10 p-4 text-error"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="name" className="mb-1 block text-sm font-medium text-slate-700">
              {t('auth.register.name')}
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('auth.register.namePlaceholder')}
              className={`w-full rounded-2xl border px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                errors.name ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
              }`}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'name-error' : undefined}
            />
            {errors.name && (
              <p id="name-error" className="text-sm text-error mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-slate-700">
              {t('auth.register.email')}
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder={t('auth.register.emailPlaceholder')}
              className={`w-full rounded-2xl border px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                errors.email ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
              }`}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && (
              <p id="email-error" className="text-sm text-error mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="mb-1 block text-sm font-medium text-slate-700">
              {t('auth.register.password')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder={t('auth.register.passwordPlaceholder')}
                className={`w-full rounded-2xl border px-4 py-3 pr-12 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                  errors.password ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
                }`}
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? 'password-error' : 'password-hint'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {/* Password strength indicator */}
            {formData.password && (
              <div className="mt-2">
                <div className="h-1 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className={`h-full ${passwordStrength.color} transition-all duration-300`}
                    style={{ width: passwordStrength.width }}
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {t('auth.passwordStrength.label')}: <span className={passwordStrength.color.replace('bg-', 'text-')}>{passwordStrength.label}</span>
                </p>
              </div>
            )}
            {errors.password && (
              <p id="password-error" className="text-sm text-error mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.password}
              </p>
            )}
            {!errors.password && !formData.password && (
              <p id="password-hint" className="mt-1 text-xs text-slate-500">
                {t('auth.register.passwordPlaceholder')}
              </p>
            )}
          </div>

          {/* Password Confirm */}
          <div>
            <label htmlFor="passwordConfirm" className="mb-1 block text-sm font-medium text-slate-700">
              {t('auth.register.passwordConfirm')}
            </label>
            <div className="relative">
              <input
                id="passwordConfirm"
                type={showPasswordConfirm ? 'text' : 'password'}
                value={formData.passwordConfirm}
                onChange={(e) => handleChange('passwordConfirm', e.target.value)}
                placeholder={t('auth.register.passwordConfirmPlaceholder')}
                className={`w-full rounded-2xl border px-4 py-3 pr-12 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                  errors.passwordConfirm ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
                }`}
                aria-invalid={!!errors.passwordConfirm}
                aria-describedby={errors.passwordConfirm ? 'passwordConfirm-error' : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                aria-label={showPasswordConfirm ? t('auth.hidePassword') : t('auth.showPassword')}
              >
                {showPasswordConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.passwordConfirm && (
              <p id="passwordConfirm-error" className="text-sm text-error mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.passwordConfirm}
              </p>
            )}
          </div>

          {/* Program Select (Optional) */}
          <div>
            <label htmlFor="program" className="mb-1 block text-sm font-medium text-slate-700">
              {t('auth.register.program')} <span className="text-slate-400">{t('auth.register.programOptional')}</span>
            </label>
            <div className="relative">
              <select
                id="program"
                value={formData.program}
                onChange={(e) => handleChange('program', e.target.value)}
                className="w-full appearance-none rounded-2xl border border-primary/20 bg-white px-4 py-3 pr-10 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="I">{t('auth.tryFree.programOptions.1')}</option>
                <option value="II">{t('auth.tryFree.programOptions.2')}</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            </div>
          </div>

          {/* Terms Checkbox */}
          <div className="flex items-start gap-3">
            <div className="flex items-center h-6">
              <input
                id="acceptTerms"
                type="checkbox"
                checked={formData.acceptTerms}
                onChange={(e) => handleChange('acceptTerms', e.target.checked)}
                className={`w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer ${
                  errors.acceptTerms ? 'border-error' : ''
                }`}
                aria-invalid={!!errors.acceptTerms}
                aria-describedby={errors.acceptTerms ? 'terms-error' : undefined}
              />
            </div>
            <label htmlFor="acceptTerms" className="cursor-pointer text-sm text-slate-700">
              {t('auth.register.acceptTerms')}{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                {t('auth.register.termsLink')}
              </a>{' '}
              {t('auth.register.and')}{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                {t('auth.register.privacyLink')}
              </a>
            </label>
          </div>
          {errors.acceptTerms && (
            <p id="terms-error" className="text-sm text-error flex items-center gap-1 -mt-2">
              <AlertCircle className="w-4 h-4" />
              {errors.acceptTerms}
            </p>
          )}

          {/* Age Declaration Checkbox (GDPR Art. 8 / UAVG Art. 16) */}
          <div className="flex items-start gap-3">
            <div className="flex items-center h-6">
              <input
                id="ageConfirm"
                type="checkbox"
                checked={formData.ageConfirm}
                onChange={(e) => handleChange('ageConfirm', e.target.checked)}
                className={`w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer ${
                  errors.ageConfirm ? 'border-error' : ''
                }`}
                aria-invalid={!!errors.ageConfirm}
                aria-describedby={errors.ageConfirm ? 'age-error' : undefined}
              />
            </div>
            <label htmlFor="ageConfirm" className="cursor-pointer text-sm text-slate-700">
              {t('auth.register.ageConfirmLabel')}
            </label>
          </div>
          {errors.ageConfirm && (
            <p id="age-error" className="text-sm text-error flex items-center gap-1 -mt-2">
              <AlertCircle className="w-4 h-4" />
              {errors.ageConfirm}
            </p>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="large"
            className="w-full"
            loading={isSubmitting}
            disabled={isSubmitting}
            icon={<UserPlus className="w-5 h-5" />}
          >
            {isSubmitting ? `${t('auth.register.cta')}...` : t('auth.register.cta')}
          </Button>

          {/* OAuth Buttons */}
          <OAuthButtons action="register" />
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

export default RegisterPage;
